<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import confusedEmotion from "@/assets/images/emotions/confused.svg";
import { safeParse } from "@/utils/accessControl";
import { hasAnyMenuAccess } from "@/components/sideMenuItems";
import { useAuthStore } from "@/stores/auth";

const route = useRoute();
const authStore = useAuthStore();

// Set by the router guard so the page can name the page that was refused.
const attemptedPath = computed(() => {
  const from = route.query.from;
  return typeof from === "string" && from.startsWith("/") ? from : null;
});

// Same rule NotFoundView uses: only offer "Back to dashboard" when the account
// actually has a reachable page, otherwise the link loops back into the guard
// that sent them here.
const canReachDashboard = computed(() => {
  const userDetails = safeParse(localStorage.getItem("user"));
  return hasAnyMenuAccess(userDetails, process.env.VUE_APP_SUPER_ADMIN_EMAIL);
});
</script>

<template>
  <div class="denied-page">
    <div class="denied-inner">
      <h1 class="denied-code">403</h1>

      <p class="denied-title">Access denied</p>

      <p class="denied-message">
        You don't have permission to view this page. If you think you should,
        ask your company administrator to update your role.
      </p>

      <p v-if="attemptedPath" class="denied-path">
        <span class="denied-path__label">Requested</span>
        <code class="denied-path__value">{{ attemptedPath }}</code>
      </p>

      <router-link v-if="canReachDashboard" to="/" class="denied-btn">
        <span>Back to dashboard</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M2.5 7h9M7.5 3l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </router-link>
      <button v-else type="button" class="denied-btn" @click="authStore.logout()">
        <span>Log out</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M2.5 7h9M7.5 3l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="denied-signal" aria-hidden="true">
        <div class="denied-signal__radar">
          <span class="denied-signal__ring denied-signal__ring--1"></span>
          <span class="denied-signal__ring denied-signal__ring--2"></span>
          <span class="denied-signal__ring denied-signal__ring--3"></span>
        </div>

        <img :src="confusedEmotion" alt="" class="denied-signal__icon" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Mirrors NotFoundView's layout so the two error pages read as one family;
   the accent shifts from primary blue to a warning amber to signal "refused"
   rather than "missing". */
.denied-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 50% 0%, rgba(217, 119, 6, 0.09) 0%, var(--neutral-50) 55%);
  padding: var(--spacing-8) var(--spacing-4);
}

.denied-inner {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.denied-code {
  font-family: "lato-black", Lato, sans-serif;
  font-size: clamp(88px, 18vw, 148px);
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  margin: 0 0 4px;
  background: linear-gradient(180deg, #b45309 0%, #f59e0b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  opacity: 0;
  animation: denied-rise 0.55s ease-out forwards;
}

.denied-title {
  font-family: "lato-black", Lato, sans-serif;
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--neutral-800, #1f2937);
  margin: 0 0 10px;
  opacity: 0;
  animation: denied-rise 0.6s ease-out 0.06s forwards;
}

.denied-message {
  font-family: Lato, sans-serif;
  font-size: 15.5px;
  line-height: 1.6;
  color: var(--neutral-600);
  max-width: 380px;
  margin: 0 0 20px;
  opacity: 0;
  animation: denied-rise 0.6s ease-out 0.1s forwards;
}

.denied-path {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 28px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(217, 119, 6, 0.1);
  border: 1px solid rgba(217, 119, 6, 0.22);
  max-width: 100%;
  opacity: 0;
  animation: denied-rise 0.6s ease-out 0.14s forwards;
}

.denied-path__label {
  font-family: Lato, sans-serif;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b45309;
  flex-shrink: 0;
}

.denied-path__value {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  color: var(--neutral-600);
  overflow-wrap: anywhere;
}

.denied-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border: none;
  cursor: pointer;
  border-radius: var(--border-radius-md, 10px);
  background: var(--primary-600);
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 24px -12px var(--primary-600);
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
  opacity: 0;
  animation: denied-rise 0.6s ease-out 0.18s forwards;
}

.denied-btn svg {
  transition: transform 0.18s ease;
}

.denied-btn:hover {
  background: var(--primary-700);
  transform: translateY(-1px);
  box-shadow: 0 14px 28px -12px var(--primary-700);
}

.denied-btn:hover svg {
  transform: translateX(2px);
}

.denied-signal {
  position: relative;
  width: 96px;
  height: 96px;
  margin-top: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  animation: denied-rise 0.7s ease-out 0.28s forwards;
}

.denied-signal__radar {
  position: absolute;
  inset: 0;
}

.denied-signal__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(217, 119, 6, 0.42);
  opacity: 0;
  animation: denied-radar 3.6s ease-out infinite;
}

.denied-signal__ring--2 { animation-delay: 1.2s; }
.denied-signal__ring--3 { animation-delay: 2.4s; }

.denied-signal__icon {
  position: relative;
  z-index: 1;
  width: 88px;
  height: 88px;
  object-fit: contain;
}

@keyframes denied-rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes denied-radar {
  0% { transform: scale(0.5); opacity: 0.55; }
  100% { transform: scale(1.9); opacity: 0; }
}

/* Dark mode: main.scss redefines the neutral/primary tokens, so only the
   amber accent and body copy need explicit handling here. */
[data-theme="dark"] .denied-title {
  color: #e8edf5;
}

[data-theme="dark"] .denied-path {
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.28);
}

[data-theme="dark"] .denied-path__label {
  color: #fbbf24;
}

@media (max-width: 480px) {
  .denied-signal { transform: scale(0.88); margin-top: 32px; }
}

@media (prefers-reduced-motion: reduce) {
  .denied-code,
  .denied-title,
  .denied-message,
  .denied-path,
  .denied-btn,
  .denied-signal {
    animation: none;
    opacity: 1;
  }
  .denied-signal__ring {
    animation: none;
    opacity: 0.5;
  }
}
</style>
